<template>
  <lateral-modal
    :title="$t(filtersModalHeaderLabel)"
    :modalClass="'ModalBox--r-filters'"
    :footerClass="'ModalBox--footer w-100 d-flex flex-column ModalBox--r--footer'"
    @close="handleClose"
  >
    <template slot="extraAction">
      <div
        v-if="!quickFilterId"
        class="Link Link--action Button Button--cancel"
        @click="reset"
        v-t.preserve="{ path: 'ACTION_CLEAR' }"
      ></div>
      <dropdown
        v-else
        :classes="{ 'transaction-dropdown': true }"
        :closeOnClickInside="false"
        :contentClasses="'Fuell-Dropdown--content--less-padding'"
      >
        <div class="Fuell-Dropdown--toggle-switch ">
          <span
            class="text-remove clickable"
            @click="handleRemoveQuickFilter"
          >{{ $t('transactions_filter_quick_filter_dropdown_action_remove') }}</span>
        </div>
      </dropdown>

    </template>
    <template slot="content">
      <form
        v-if="!loading"
        class="Form px-2"
      >
        <div class="row">
          <div
            class="col-12"
            v-if="isSuperBookKeeper"
          >
            <div class="input--block input--block--margin input--block--padded">
              <label
                for=""
                class="form--label"
              > {{ $t('transactions_filter_customer').toUpperCase() }}</label>
              <div class="multiselect-filter">
                <multi-select-input
                  :value="customerId"
                  :options="customers"
                  :noResultLabel="'multiselect_no_budgets_found'"
                  :noOptionsLabel="'multiselect_no_budgets_found'"
                  searchable
                  :placeholder="$t('transactions_filter_customer_search')"
                  :isSearching="isSearchingCustomers"
                  @asyncFind="asyncCustomers"
                  @close="close"
                  @inputSelected="setCustomerAndFetchFiltersData($event)"
                ></multi-select-input>
              </div>
            </div>
          </div>
          <div
            class="col-12"
            v-if="((isUser && isOwnerOfBudget) || !isUser) && !expenses"
          >

            <div class="input--block input--block--margin input--block--padded">
              <label
                for=""
                class="form--label"
              >
                {{ $t('USER').toUpperCase() }}</label>

              <div class="multiselect-filter multiselect-tags">
                <multiselect
                  :loading="isSearchingUsers"
                  :internal-search="false"
                  :clear-on-select="true"
                  :close-on-select="true"
                  :hide-selected="true"
                  :options="users"
                  :customLabel="customLabelUser"
                  :placeholder="$t('transactions_filter_user_search')"
                  :noResultLabel="'multiselect_no_users_found'"
                  :noOptionsLabel="'multiselect_no_users'"
                  :limitText="() => ''"
                  :limit="usersLimitTags"
                  :value="usersSelected"
                  :showNoResults="showNoResults"
                  :showNoOptions="showNoResults"
                  track-by="id"
                  multiple
                  selectLabel=""
                  deselectLabel=""
                  searchable
                  tag-placeholder=""
                  @input="addUser($event)"
                  @search-change="debounceSearch"
                  @close="close"
                >
                  <div
                    class="caret"
                    slot="caret"
                    slot-scope="{ toggle }"
                    @mousedown.prevent.stop="toggle"
                  >
                    <i
                      class="Ctrl Ctrl--16"
                      aria-hidden="true"
                    >
                      <svg
                        focusable="false"
                        aria-hidden="true"
                        aria-label="Edit"
                      >
                        <use xlink:href="#dropdown-icon"></use>
                      </svg>
                    </i>
                  </div>
                  <template slot="beforeList">
                    <div class="w-100 d-flex dropdown-before-list--block">

                      <toggle-switch
                        :id="'users-deleted-at-filter-switch'"
                        :status.sync="showDeletedUsersFlag"
                        :negativeLabel="'transactions_filter_users_show_deleted_option'"
                        :negativeLabelClass="'Label Label--text mr-2'"
                      />

                    </div>
                    <div class="dropdown-separator-block">
                      <div class="dropdown-separator-block--separator-bar">
                      </div>
                    </div>
                  </template>
                  <template
                    slot="tag"
                    slot-scope="props"
                  >
                    <span class="multiselect__tag"><span>{{ customLabelUser(props.option) }}</span>
                      <i
                        aria-hidden="true"
                        tabindex="1"
                        class="multiselect__tag-icon"
                        @click="props.remove(props.option)"
                      ></i>
                    </span>
                    <strong
                      v-if="usersSelected.length > usersLimitTags"
                      class="multiselect__strong"
                    >{{ usersLimitText() }}</strong>

                  </template>
                  <template
                    slot="option"
                    slot-scope="props"
                  >
                    <div class="d-flex justify-content-between">
                      <span>{{ customLabelUser(props.option)}}</span>
                      <span
                        class="multiselect-deleted-label"
                        v-if="props.option.status.statusType.code === 'DELETED'"
                      > {{$t('transactions_filter_users_deleted_user')}}</span>
                    </div>
                  </template>
                  <template slot="noResult">
                    <span>{{ $t('user_finder_selector_no_results') }}</span>
                  </template>
                  <template slot="noOptions">
                    <span> {{ $t('user_finder_selector_no_results') }}</span>
                  </template>
                </multiselect>
              </div>
            </div>
          </div>
          <template v-if="!userInAuthorizations">
            <div class="col-6">
              <div class="input--block input--block--margin input--block--padded">
                <label class="form--label">
                  {{ $t('FILTER_AMOUNT_FROM').toUpperCase() }}</label>
                <div
                  class="Form-field"
                  v-focus-input
                >
                  <input
                    class="Input-text"
                    type="number"
                    min="0"
                    @keypress="handleNonNumeric($event)"
                    @blur="handleAmountBlur($event, 'amountGte')"
                    v-model.number="amountGte"
                    :placeholder="$t('transactions_edit_transaction_amount')"
                  />
                </div>
              </div>
            </div>
            <div class="col-6">
              <div class="input--block input--block--margin input--block--padded">
                <label class="form--label">
                  {{ $t('FILTER_AMOUNT_TO').toUpperCase() }}</label>
                <div
                  class="Form-field"
                  v-focus-input
                >
                  <input
                    class="Input-text"
                    type="number"
                    min="0"
                    @keypress="handleNonNumeric($event)"
                    @blur="handleAmountBlur($event, 'amountLte')"
                    v-model.number="amountLte"
                    :placeholder="$t('transactions_edit_transaction_amount')"
                  />
                </div>
              </div>
            </div>
          </template>

          <div class="col-12 mt-1">
            <label class="form--label mx-2">
              {{
                $t('transactions_filter_transaction_status').toUpperCase()
              }}</label>
            <ul class="Tabs mt-1 mb-2 d-flex mx-2">
              <li class="Tabs-itm flex-grow-1 Tab-item-first">
                <a
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': categorizeStatus === undefined }"
                  href="#"
                  @click.prevent="setField('categorizeStatus', undefined)"
                  v-t.preserve="{ path: 'transactions_categorize_status__ALL' }"
                ></a>
              </li>
              <li class="Tabs-itm flex-grow-1 Tab-item-second">
                <a
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': categorizeStatus === true }"
                  href="#"
                  @click.prevent="setField('categorizeStatus', true)"
                  v-t.preserve="{
                    path: 'transactions_categorize_status__COMPLETED'
                  }"
                ></a>
              </li>
              <li class="Tabs-itm flex-grow-1 Tab-item-third">
                <a
                  href="#"
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': categorizeStatus === false }"
                  @click.prevent="setField('categorizeStatus', false)"
                  v-t.preserve="{
                    path: 'transactions_categorize_status__INCOMPLETED'
                  }"
                ></a>
              </li>
            </ul>
          </div>
          <template v-if="reviewFlows.length === 0">
          <div
            class="col-12 mt-1">
            <label class="form--label mx-2">
              {{
                $t('transactions_filter_transaction_review_status').toUpperCase()
              }}</label>
            <ul class="Tabs mt-1 mb-2 d-flex mx-2">
              <li class="Tabs-itm flex-grow-1 Tab-item-first">
                <a
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': checkReviewStatus('ALL') }"
                  href="#"
                  @click.prevent="setField('reviewStatus', 'ALL')"
                  v-t.preserve="{ path: 'transactions_review_status__ALL' }"
                ></a>
              </li>
              <li class="Tabs-itm flex-grow-1 Tab-item-second">
                <a
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': checkReviewStatus('REVIEWED') }"
                  href="#"
                  @click.prevent="setField('reviewStatus', 'REVIEWED')"
                  v-t.preserve="{
                    path: 'transactions_review_status__REVIEWED'
                  }"
                ></a>
              </li>
              <li class="Tabs-itm flex-grow-1 Tab-item-third">
                <a
                  href="#"
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': checkReviewStatus('NOT_REVIEWED') }"
                  @click.prevent="setField('reviewStatus', 'NOT_REVIEWED')"
                  v-t.preserve="{
                    path: 'transactions_review_status__NOT_REVIEWED'
                  }"
                ></a>
              </li>
            </ul>
          </div>

                    <div
            class="col-12 mt-1"  v-if="userIsInTransactionsSections">
            <label class="form--label mx-2">
              {{
                $t('transactions_filter_transaction_personal_expense').toUpperCase()
              }}</label>
            <ul class="Tabs mt-1 mb-2 d-flex mx-2">
              <li class="Tabs-itm flex-grow-1 Tab-item-first">
                <a
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': !personalExpenseRegularFilterFlag }"
                  href="#"
                  @click.prevent="setField('personalExpenseRegularFilterFlag', false)"
                  v-t.preserve="{ path: 'transactions_review_status__ALL' }"
                ></a>
              </li>
              <li class="Tabs-itm flex-grow-1 Tab-item-second">
                <a
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': personalExpenseRegularFilterFlag }"
                  href="#"
                  @click.prevent="setField('personalExpenseRegularFilterFlag', true)"
                  v-t.preserve="{
                    path: 'transactions_review_status__PERSONAL_EXPENSE'
                  }"
                ></a>
              </li>
            </ul>
          </div>
          </template>
          <div
            class="col-12 mt-1"
            v-if="isBookKeeper || isAdmin"
          >
            <label class="form--label mx-2">
              {{
                $t('transactions_filter_transaction_account').toUpperCase()
              }}</label>
            <ul class="Tabs mt-1 mb-2 d-flex mx-2">
              <li class="Tabs-itm flex-grow-1 Tab-item-first">
                <a
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': accountedAt === undefined}"
                  href="#"
                  @click.prevent="setField('accountedAt', undefined)"
                  v-t.preserve="{ path: 'transactions_filter_transaction_account_ALL' }"
                ></a>
              </li>
              <li class="Tabs-itm flex-grow-1 Tab-item-second">
                <a
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': accountedAt === true }"
                  href="#"
                  @click.prevent="setField('accountedAt', true)"
                  v-t.preserve="{
                    path: 'transactions_filter_transaction_account_ACCOUNTED'
                  }"
                ></a>
              </li>
              <li class="Tabs-itm flex-grow-1 Tab-item-third">
                <a
                  href="#"
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': accountedAt === false}"
                  @click.prevent="setField('accountedAt', false)"
                  v-t.preserve="{
                    path: 'transactions_filter_transaction_account_UNACCOUNTED'
                  }"
                ></a>
              </li>
            </ul>
          </div>
          <div
            v-if="(isAdmin || isBookKeeper || isOwnerOfBudget) && !expenses"
            class="col-12 mt-1"
          >
            <label class="form--label mx-2">
              {{
                $t('transactions_filter_transaction_warning').toUpperCase()
              }}</label>
            <ul class="Tabs mt-1 mb-2 d-flex mx-2">
              <li class="Tabs-itm flex-grow-1 Tab-item-first">
                <a
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': withWarnings === undefined }"
                  href="#"
                  @click.prevent="setField('withWarnings', undefined)"
                  v-t.preserve="{ path: 'transactions_warning__ALL' }"
                ></a>
              </li>
              <li class="Tabs-itm flex-grow-1 Tab-item-second">
                <a
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': withWarnings === true }"
                  href="#"
                  @click.prevent="setField('withWarnings', true)"
                  v-t.preserve="{
                    path: 'transactions_warning__WITH_WARNINGS'
                  }"
                ></a>
              </li>
              <li class="Tabs-itm flex-grow-1 Tab-item-third">
                <a
                  href="#"
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': withWarnings === false }"
                  @click.prevent="setField('withWarnings', false)"
                  v-t.preserve="{
                    path: 'transactions_review_status__NO_WARNINGS'
                  }"
                ></a>
              </li>
            </ul>
          </div>
          <collapsable-item
            v-if="reviewFlows.length > 0"
            class="col-12"
            :display="hasAnyReviewFilter"
          >
            <template v-slot:header="{ showContent }">
              <label
                for=""
                class="form--label form--label--placeholder mx-2"
              >{{ $t('transactions_filter_review_status').toUpperCase() }}</label>
              <template v-if="!showContent">
                <div class="Form-field Form-field--review-block Form-field--placeholder d-flex justify-content-between align-items-center mx-2">
                  <span
                    class="filter-placeholder--active"
                    v-if="hasAnyReviewFilter"
                  >{{ $t('transactions_filter_review_status_placeholder_check_selected_filters') }}</span>
                  <span
                    class="filter-placeholder"
                    v-else
                  >{{ $t('transactions_filter_review_status_placeholder') }}</span>
                  <i
                    class="Ctrl Ctrl--16"
                    aria-hidden="true"
                  >
                    <svg
                      focusable="false"
                      aria-hidden="true"
                      aria-label="Edit"
                    >
                      <use xlink:href="#dropdown-icon"></use>
                    </svg>
                  </i>
                </div>
              </template>
              <template v-else>
                <div class="Form-field--review-block Form-field--placeholder d-flex justify-content-end align-items-center arrow-content-container">

                  <i
                    class="Ctrl Ctrl--16"
                    aria-hidden="true"
                  >
                    <svg
                      focusable="false"
                      aria-hidden="true"
                      aria-label="Edit"
                    >
                      <use xlink:href="#dropdown-icon"></use>
                    </svg>
                  </i>

                </div>
              </template>

            </template>
            <div
              class="mx-1 content-shown"
              slot="content"
            >
              <div class="Form-field Form-field--review-block input--block">
                <ul class="review-flow--tree mb-2">
                  <!-- NORMAL-->
                  <li class="list-header d-flex justify-content-betweeb">
                    <input-checkbox
                      id="reviewed-normal"
                      :value="reviewFilterTypeNormal.checked"
                      :indeterminate="normalReviewIndeterminateStatus"
                      @click="setReviewFlowStepType('NORMAL')"
                    >
                      <span
                        slot="input-label"
                        class="list-header"
                      >
                        {{ $t('transactions_filter_review_status_flow_header_reviewed') }}
                      </span>
                    </input-checkbox>

                  </li>
                  <li class="label-tree">
                    <input-checkbox
                      :id="'review-flow-filter-approved'"
                      :value="reviewStatusApproved.checked"
                      @click="toggleReviewFilter('reviewStatusApproved', REVIEW_STATUS.APPROVED)"
                    >
                      <span
                        slot="input-label"
                        class="option"
                      >
                        {{ $t('transactions_filter_review_status_flow_option_approved') }}
                      </span>
                    </input-checkbox>
                  </li>

                  <!-- Only for transactions -->
                  <li class="label-tree label-tree-bottom" v-if="userIsInTransactionsSections">
                    <input-checkbox
                      :id="'review-flow-filter-cancelled'"
                      :value="reviewStatusPersonalExpense.checked"
                      @click="toggleReviewFilter('reviewStatusPersonalExpense', REVIEW_STATUS.PERSONAL_EXPENSE)"
                    >
                      <span
                        slot="input-label"
                        class="label-tree-normal"
                      >
                        {{ $t('transactions_filter_review_status_flow_option_personal_expense') }}
                      </span>
                    </input-checkbox>
                  </li>

                  <!-- Only for expenses -->
                  <li class="label-tree label-tree-bottom" v-else>
                    <input-checkbox
                      :id="'review-flow-filter-cancelled'"
                      :value="reviewStatusCancelled.checked"
                      @click="toggleReviewFilter('reviewStatusCancelled', REVIEW_STATUS.CANCELLED)"
                    >
                      <span
                        slot="input-label"
                        class="label-tree-normal"
                      >
                        {{ $t('transactions_filter_review_status_flow_option_rejected') }}
                      </span>
                    </input-checkbox>
                  </li>
                  <!-- FLUJOS -->
                  <li class="list-header">
                    <input-checkbox
                      :id="'reviewed-funnel'"
                      :value="reviewFilterTypeFunnel.checked"
                      :indeterminate.sync="funnelIndeterminateStatus"
                      @click="setReviewFlowStepType('FUNNEL')"
                    >
                      <span
                        class="list-header"
                        slot="input-label"
                      >
                        {{ $t('transactions_filter_review_status_flow_list_header_in_review') }}
                      </span>

                    </input-checkbox>
                  </li>
                  <!-- Flujos -->
                  <li class="label-tree label-tree-title mt-1">{{ $t('transactions_review_flow_step_default') }}
                    <info-display
                      :iconClass="'info-container'"
                      :infoText="$t('transactions_review_flow_step_default_tooltip')"
                    />
                  </li>
                  <li class="label-tree mb-2">
                    <input-checkbox
                      :id="`reviewFlowDefault`"
                      :value="reviewFilterDefaultFlow.checked"
                      @click="toggleReviewFilter('reviewFilterDefaultFlow', REVIEW_STATUS.AWAITING_REVIEW)"
                    >
                      <span
                        class="label-tree-normal"
                        slot="input-label"
                      >{{
                        $t('transactions_review_status__AWAITING_REVIEW') }}</span>
                    </input-checkbox>
                  </li>
                  <!--- FLUJOS MULTINIVEL -->
                  <ul
                    v-for="reviewFlow in reviewFlows"
                    :key="reviewFlow.id"
                    class="review-flow--tree"
                  >
                    <li>
                      <span
                        class="label-tree label-tree-title"
                        slot="input-label"
                      > {{ reviewFlow.name }}
                      </span>
                    </li>
                    <ul class="review-flow--tree-step-container">
                      <li
                        v-for="(step) in reviewFlow.steps"
                        :key="step.id"
                        class="review-flow--tree-step"
                      >
                        <input-checkbox
                          :id="`reviewFlowStep${step.id}`"
                          :value.sync="step.selected"
                          @click="setReviewFlowStep(step)"
                        >
                          <span
                            class="label-tree-normal"
                            slot="input-label"
                          >{{ step.name }}</span>
                        </input-checkbox>
                      </li>
                    </ul>
                  </ul>
                </ul>

                <!----->
              </div>
            </div>
          </collapsable-item>
          <div
            v-if="showPaymentMethodsFilter"
            class="col-12 mt-2"
          >
            <template>
              <div class="input--block input--block--margin input--block--padded">
                <label
                  for="accounting-period"
                  class="form--label"
                >
                  {{ $t('transactions_payment_methods').toUpperCase() }}
                </label>
                <div class="multiselect-filter">
                  <multiselect
                    :id="'expense-payment-method-selector'"
                    :options="paymentMethods"
                    :customLabel="(option) => paymentMethodsLabel(option)"
                    :value="paymentMethod"
                    :placeholder="$t('transactions_payment_methods')"
                    track-by="id"
                    :allowEmpty="false"
                    :showLabels="false"
                    @input="handlePaymentMethodSelect"
                  >
                    <div
                      class="caret"
                      slot="caret"
                      slot-scope="{ toggle }"
                      @mousedown.prevent.stop="toggle"
                    >
                      <i
                        class="Ctrl Ctrl--16"
                        aria-hidden="true"
                      >
                        <svg
                          focusable="false"
                          aria-hidden="true"
                          e
                          aria-label="Edit"
                        >
                          <use xlink:href="#dropdown-icon"></use>
                        </svg>
                      </i>
                    </div>
                    <template v-slot:noOptions>
                      <span>{{ $t('multiselect_no_payment_methods') }}</span>
                    </template>
                    <template v-slot:noResult>
                      <span>{{ $t('multiselect_payment_method_not_found') }}</span>
                    </template>
                    <template
                      slot="singleLabel"
                      slot-scope="props"
                    >
                      <div
                        class="full-width">
                        <span>{{ props.option.name }}</span>
                        <span class="multiselect--extra-info-gray">
                          -
                          {{
                            props.option.repayable
                            ? $t("expenses_expense_is_reimbursable")
                            : $t("expenses_expense_is_not_reimbursable")
                          }}</span>
                      </div>
                    </template>
                  </multiselect>
                </div>
              </div>
            </template>
          </div>
          <!--
            TODO: Card not implemented yet -> BETA
          <div class="col-12">
            <div class="Form-field">
              <div class="Select">
                <select class="Select-ctrl Select-ctrl--table Select-ctrl--unselected">
                  <option selected="selected" disabled="disabled">Card</option>
                  <option>---</option>
                </select><i class="Select-icon">
                  <svg focusable="false" aria-hidden="true" aria-label="Edit">
                    <use xlink:href="#dropdown-icon"></use>
                  </svg></i>
              </div>
            </div>
          </div>
          -->

          <div
            v-if="!hideBudget"
            class="col-12 mt-2"
          >
            <div class="input--block input--block--margin input--block--padded">
              <label
                for=""
                class="form--label"
              >{{
                $t('BUDGETS').toUpperCase()
              }}</label>
              <div class="multiselect-filter">
                <multiselect
                  :loading="isSearchingBudgets"
                  :internal-search="false"

                  :close-on-select="true"
                  :hideSelected="true"
                  :options="budgets"
                  :customLabel="(option) => option.name"
                  :placeholder="$t('transactions_filter_budgets_search')"
                  :noResultLabel="'multiselect_no_budgets_found'"
                  :noOptionsLabel="'multiselect_no_budgets_found'"
                  :value="budgetsSelected"
                  track-by="id"
                  multiple
                  selectLabel=""
                  deselectLabel=""
                  searchable
                  :showNoResults="showNoBudgetResult"
                  :showNoOptions="showNoBudgetResult"
                  @input="setBudgets($event)"
                  @search-change="debounceBudgetSearch"
                  @remove="removeBudget"

                >

                  <template slot="beforeList">
                    <div class="w-100 d-flex dropdown-before-list--block">

                      <toggle-switch
                        :id="'budgets-archived-at-filter-switch'"
                        :status.sync="showArchivedBudgetsFlag"
                        :negativeLabel="'transactions_filter_budgets_show_archived_option'"
                        :negativeLabelClass="'Label Label--text mr-2'"
                      />

                    </div>
                    <div class="dropdown-separator-block">
                      <div class="dropdown-separator-block--separator-bar">
                      </div>
                    </div>
                  </template>
                  <template
                    slot="option"
                    slot-scope="props"
                  >
                    <div class="d-flex justify-content-between">
                      <span>{{ props.option.name}}</span>
                      <span
                        class="multiselect-deleted-label"
                        v-if="props.option.status.code === 'ARCHIVED'"
                      > {{$t('budgets_budget_status__ARCHIVED')}}</span>
                    </div>
                  </template>

                  <template slot="noResult">
                    <span>{{ $t('user_finder_selector_no_results') }}</span>
                  </template>
                  <template slot="noOptions">
                    <span> {{ $t('user_finder_selector_no_results') }}</span>
                  </template>
                  <div
                    class="caret"
                    slot="caret"
                    slot-scope="{ toggle }"
                    @mousedown.prevent.stop="toggle"
                  >
                    <i
                      class="Ctrl Ctrl--16"
                      aria-hidden="true"
                    >
                      <svg
                        focusable="false"
                        aria-hidden="true"
                        e
                        aria-label="Edit"
                      >
                        <use xlink:href="#dropdown-icon"></use>
                      </svg>
                    </i>
                  </div>
                </multiselect>
              </div>
            </div>
          </div>

          <div class="col-12 mt-2">
            <div class="input--block input--block--margin input--block--padded">
              <label
                for=""
                class="form--label"
              >
                {{ $t('transactions_edit_transaction_category').toUpperCase() }}
              </label>
              <div class="multiselect-filter">
                <category-selector
                  :id="'transaction-category-selector'"
                  filterMode
                  :selectedCategoryObject.sync="selectedCategoryObject"
                  @categorySelected="handleCategorySubcategorySelect"
                />

              </div>
            </div>
          </div>

          <div
            class="col-12 mt-2"
            v-if="!isUser && !isSuperBookKeeper"
          >
            <template>
              <div class="input--block input--block--margin input--block--padded">
                <label
                  for="accounting-period"
                  class="form--label"
                >
                  {{
                    $t(
                      'transactions_filter_transaction_accounting_period'
                    ).toUpperCase()
                  }}
                </label>
                <div class="multiselect-filter">
                  <multi-select-input
                    id="accounting-period"
                    searchable
                    :value="accountingPeriodId"
                    :options="accountingPeriods"
                    :noResultLabel="'multiselect_accounting_period_not_found'"
                    :noOptionsLabel="'multiselect_no_accounting_periods'"
                    :placeholder="
                      $t('transactions_filter_transaction_accounting_period')
                    "
                    emitFullValue
                    @inputSelected="handleAccountingPeriodSelect"
                  ></multi-select-input>
                </div>
              </div>
            </template>
          </div>
          <template v-if="!isSuperBookKeeper">
            <div class="col-12 mt-2">
              <div class="input--block input--block--margin input--block--padded">
                <label
                  for="tax-type"
                  class="form--label"
                >
                  {{
                    $t('transactions_filter_transaction_tax_type').toUpperCase()
                  }}
                </label>

                <div class="multiselect-filter">
                  <multi-select-input
                    id="tax-type"
                    :value="taxType"
                    :options="taxTypes"
                    :emitFullValue="true"
                    :placeholder="$t('transactions_filter_transaction_tax_type')"
                    @inputSelected="handleTaxTypeSelect"
                  ></multi-select-input>
                </div>
              </div>
            </div>
          </template>

          <div class="col-12 mt-3">
            <label class="form--label mx-2">
              {{
                $t('transactions_filter_transaction_is_bill').toUpperCase()
              }}</label>
            <ul class="Tabs mt-1 mb-2 d-flex mx-2">
              <li class="Tabs-itm flex-grow-1 Tab-item-first">
                <a
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': isBill === RECEIPT_TYPES.ALL }"
                  href="#"
                  @click.prevent="setField('isBill', RECEIPT_TYPES.ALL)"
                  v-t.preserve="{ path: 'transactions_filter_is_bill_all' }"
                ></a>
              </li>
              <li class="Tabs-itm flex-grow-1 Tab-item-second">
                <a
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': isBill === RECEIPT_TYPES.BILL }"
                  href="#"
                  @click.prevent="setField('isBill', RECEIPT_TYPES.BILL)"
                  v-t.preserve="{
                    path: 'transactions_filter_is_bill_only_bills'
                  }"
                ></a>
              </li>
              <li class="Tabs-itm flex-grow-1 Tab-item-third">
                <a
                  href="#"
                  class="Tabs-itmLink"
                  :class="{ 'Tabs-itmLink--curr': isBill === RECEIPT_TYPES.RECEIPT }"
                  @click.prevent="setField('isBill', RECEIPT_TYPES.RECEIPT)"
                  v-t.preserve="{
                    path: 'transactions_filter_is_bill_only_receipts'
                  }"
                ></a>
              </li>
            </ul>
          </div>
        </div>
      </form>
    </template>
    <template
      slot="footer"
      v-if="!loading"
    >
      <div
        class="mb-2"
        v-if="!quickFilterId && !this.isBudget"
      >
        <toggle-switch
          :id="'create-new-quick-filter-view'"
          :status.sync="createNewQuickFilterFlag"
          :label="''"
          useLabelSlot
          :labelClass="'Label Label--text'"
        >

          <template slot="label">
            <i18n
              tag="span"
              class="Fuell-Text Fuell-Text--smaller"
              path="transactions_filter_create_filtered_view"
            >
              <template v-slot:filteredViewSlot>
                <span class="text-primary">{{ $t('transactions_filter_create_filtered_view_slot') }}</span>
              </template>
            </i18n>
          </template>
        </toggle-switch>
      </div>

      <button
        class="Button Button--primary flex-grow-1"
        @click.prevent="handleSetFilters"
        :disabled="isSubmitting"
        href=""
        v-t.preserve="{ path: filterActionLabel }"
      ></button>
      <button
        v-if="quickFilterId"
        class="Fuell-Button blue--secondary flex-grow-1 mt-2"
        @click="showCreateQuicKFilterModal"
        :disabled="isSubmitting"
        href=""
        v-t.preserve="{ path: 'transactions_filter_quick_filter_save_as_new_quick_filter_action' }"
      ></button>
    </template>
    <template slot="modals">
      <create-new-quick-filter-modal
        :open.sync="showCreateNewQuickFilterModalFlag"
        v-if="showCreateNewQuickFilterModalFlag"
        @createFilteredView="handleCreateFilteredView"
      />
    </template>
  </lateral-modal>
</template>

<script>

import { REVIEW_STATUS } from '@/constants/reviewStatus'
import { RECEIPT_TYPES } from '../../_constants/constants'
import Multiselect from 'vue-multiselect'
import _modalHandlerMixin from '@/components/Commons/_mixins/_modalHandlerMixin'
import MultiSelectInput from '@/components/Commons/Forms/MultiSelectInput'
import { segmentTrack } from '@/analytics/segment'
import InputCheckbox from '@/components/Commons/Forms/InputCheckbox'
import { isEqual } from 'lodash-es'
import CategorySelector from '../_shared/CategorySelector.vue'
import _transactionFiltersHelperMixin from '../../_mixins/_transactionFiltersHelperMixin'
import CollapsableItem from '@/components/Commons/CollapsableItem'
import LateralModal from '@/components/Commons/Modals/LateralModal'
import CreateNewQuickFilterModal from '../_shared/CreateNewQuickFilterModal.vue'
import i18n from '@/locales'
import _reviewFlowStatusFilterMixin from '../../_mixins/funnel/_reviewFlowStatusFilterMixin'
import InfoDisplay from '@/components/Commons/InfoDisplay'
import ToggleSwitch from '@/components/Commons/Forms/ToggleSwitch'
import Dropdown from '@/components/Commons/Dropdown'
const taxTypes = [
  {
    id: 0,
    field: null,
    value: null,
    name: i18n.t('transactions_filter_tax_types_all')
  },
  { id: 1, field: 'taxBase1', value: 21, name: '21%' },
  { id: 2, field: 'taxBase2', value: 10, name: '10%' },
  { id: 3, field: 'taxBase3', value: 4, name: '4%' },
  { id: 4, field: 'taxBase4', value: 0, name: '0%' }
  /*
  { id: 2, value: 15, name: '15%' },
  { id: 5, value: 7, name: '7%' },
  { id: 4, value: 9.5, name: '9,5%' },
  { id: 7, value: 3, name: '3%' }, */
]

function initialDataState () {
  return {
    REVIEW_STATUS: REVIEW_STATUS,
    showNoResults: false,
    usersLimitTags: 3,
    dateAfter: '',
    dateBefore: '',
    transactionStatusId: '',
    categorizeStatus: undefined,
    budget: [],
    budgetId: '',
    customerId: '',
    amountGte: undefined,
    amountLte: undefined,
    range: {},
    selectedSubCategory: '',
    selectedCategoryObject: null,
    accountingPeriodId: null,
    isBill: undefined,
    isSearchingUsers: false,
    isSearchingBudgets: false,
    isSearchingCustomers: false,
    taxType: null,
    taxTypes: taxTypes,
    accountedAt: undefined,
    reviewStatus: undefined,
    withWarnings: undefined,
    isSubmitting: false,
    paymentMethod: undefined,
    reviewFlowStep: [],
    defaultReviewFlowStep: '',
    reviewStatusApproved: { checked: false },
    reviewStatusCancelled: { checked: false },
    reviewStatusPersonalExpense: { checked: false },
    reviewStatusFilter: [],
    reviewFilterTypeNormal: { name: 'normal', checked: false },
    reviewFilterTypeFunnel: { name: 'funnel', checked: false },
    reviewFilterDefaultFlow: { name: 'default', checked: false },
    filtersAmount: 0,
    createNewQuickFilterFlag: false,
    showCreateNewQuickFilterModalFlag: false,
    customFilteredView: '',
    showArchivedBudgetsFlag: false,
    showNoBudgetResult: false,
    usersQuery: '',
    budgetsQuery: '',
    personalExpenseRegularFilterFlag: false,
    reviewStatusTabStatus: 'ALL'

  }
}

export default {
  name: 'TransactionFilterModal',
  mixins: [_modalHandlerMixin, _transactionFiltersHelperMixin, _reviewFlowStatusFilterMixin],
  components: {
    MultiSelectInput,
    Multiselect,
    CategorySelector,
    InputCheckbox,
    CollapsableItem,
    InfoDisplay,
    LateralModal,
    ToggleSwitch,
    CreateNewQuickFilterModal,
    Dropdown
  },
  props: {
    expenses: {
      type: Boolean,
      default: false
    },
    quickFilterId: {
      type: [Number, String],
      default: 0
    },
    hideBudget: {
      type: Boolean,
      default: false
    },
    isBudget: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    paymentMethods () {
      return this.$store.getters['TransactionsStore/officePaymentMethods'] || []
    },
    usersSelected () {
      return this.$store.getters['FiltersStore/TransactionsStore/userFilters']
    },
    budgetsSelected () {
      return this.$store.getters['FiltersStore/TransactionsStore/budgetsFilters']
    },
    transactionStatuses () {
      return (
        this.$store.getters['ConfigStore/privateConfig'].transaction_statuses ||
        []
      )
    },
    transactionsCategorizeStatusList () {
      return this.$store.getters['TransactionsStore/categorizeStatusList']
    },
    RECEIPT_TYPES () {
      return RECEIPT_TYPES
    },

    isUser () {
      return this.$store.getters['LoginStore/isUser']
    },
    users () {
      return this.$store.getters['UsersStore/usersSearch'] || []
    },
    customers () {
      return this.$store.getters['CustomersStore/customers'] || []
    },
    budgets () {
      return this.$store.getters['BudgetsStore/budgets'].filter(el => el.type.code === 'NORMAL') || []
    },
    isOwnerOfBudget () {
      return this.$store.getters['UsersStore/userLoggedIsOwnerOfBudget']
    },
    isBookKeeper () {
      return this.$store.getters['LoginStore/isBookKeeper']
    },
    isAdmin () {
      return this.$store.getters['LoginStore/isAdmin']
    },
    isSuperBookKeeper () {
      return this.$store.getters['LoginStore/isSuperBookKeeper']
    },
    isCallingUsers () {
      return this.$store.getters['UsersStore/isCallingUsers'] || false
    },
    isCallingBudgets () {
      return this.$store.getters['BudgetsStore/isCallingBudgets'] || false
    },
    isCallingAccounting () {
      return this.$store.getters['AccountingStore/isCallingAccounting'] || false
    },
    accountingPeriods () {
      return this.$store.getters['AccountingStore/accountingPeriods'] || []
    },
    categories () {
      return this.$store.getters['CategoriesStore/categories'] || []
    },
    customCategories () {
      return this.$store.getters['CategoriesStore/customCategories'] || []
    },
    defaultCategoriesOffice () {
      return this.$store.getters['CategoriesStore/defaultCategoriesOffice'] || []
    },
    filterActionLabel () {
      return this.quickFilterId ? 'transaction_filter_quick_filter_edit_quick_filter' : 'ACTION_FILTER'
    },
    filtersModalHeaderLabel () {
      return this.quickFilterId ? 'transaction_filter_quick_filter_edit_quick_filter' : 'FILTERS'
    },
    userIsInTransactionsSections () {
      return this.transactionsRoutes.includes(this.$route.name)
    },
    categoriesAndSubcategoriesOptions () {
      const options = []
      this.categories.forEach(rawCategory => {
        options.push({
          categoryId: rawCategory.id,
          name: rawCategory.name
        })
        if (
          rawCategory.customCategories &&
          rawCategory.customCategories.length > 0
        ) {
          rawCategory.customCategories.forEach(sub => {
            options.push({
              categoryId: rawCategory.id,
              categoryName: rawCategory.name,
              subcategoryId: sub.id,
              name: sub.name
            })
          })
        }
      })
      return options
    },
    categoriesSubcategoriesOptions () {
      const categoriesOptions = []
      const rawCategories = JSON.parse(JSON.stringify(this.categories))
      rawCategories.forEach(rawCategory => {
        const categoryOptions = {
          name: rawCategory.name,
          subcategories: rawCategory.customCategories.map(customCategory => {
            return { category: rawCategory.id, ...customCategory }
          })
        }
        if (categoryOptions.subcategories.length < 1) {
          categoriesOptions.subcategories = [
            {
              category: rawCategory.id,
              ...rawCategory
            }
          ]
        }
        categoriesOptions.push(categoryOptions)
      })
      return categoriesOptions
    },
    currentFilters () {
      if (Object.keys(this.$route.query).length > 0) {
        return this.parseQueryParams()
      } else {
        return this.$store.getters['TransactionsStore/filters']
      }
    },
    userInAuthorizations () {
      return this.$route.name === 'transactions_authorizations'
    },
    currentRoute () {
      return this.$route.path
    },
    showPaymentMethodsFilter () {
      return !this.transactionsRoutes.includes(this.$route.name)
    },
    transactionsRoutes () {
      return ['my_charged_transactions', 'my_authorizations_transactions', 'my_declined_transactions', 'transactions_charged', 'transactions_declined', 'transactions_authorizations']
    },
    userId () {
      return this.$store.getters['LoginStore/user']?.id
    },
    selectedFilteredView () {
      return this.$store.getters['TransactionsStore/currentFilteredView']
    },
    showDeletedUsersFlag: {
      get () {
        return this.$store.getters['UsersStore/getDeletedUsersFlag']
      },
      set (v) {
        this.toggleDeletedUsersFlag()
      }
    }
  },
  data () {
    return {
      loading: true,
      ...initialDataState()
    }
  },
  methods: {
    paymentMethodsLabel (paymentMethod) {
      return `${paymentMethod?.name} - ${paymentMethod?.repayable ? this.$t('expenses_expense_is_reimbursable') : this.$t('expenses_expense_is_not_reimbursable')}`
    },
    setBudgets (budget) {
      if (!budget) return
      this.$store.dispatch('FiltersStore/TransactionsStore/addBudgetsFilters', budget)
    },
    removeBudget (budget) {
      const budgetWithoutBudgetToRemove = this.budget.filter(el => el.id !== budget.id)
      this.budget = [...budgetWithoutBudgetToRemove]
    },
    usersLimitText () {
      return this.$t('multiselect_limit_text', { count: this.usersSelected.length - this.usersLimitTags })
    },
    customLabelUser ({ firstName, lastName }) {
      return `${firstName} ${lastName}`
    },
    debounceSearch (event) {
      if (!event) {
        this.showNoResults = false

        this.$store.dispatch('UsersStore/clearUsersSearchList')
        return
      }
      this.showNoResults = true
      if (this.debounce) {
        this.isSearchingUsers = false
        clearTimeout(this.debounce)
      }
      this.isSearchingUsers = true
      this.debounce = setTimeout(() => {
        this.asyncUsers(event)
      }, 250)
    },
    addUser (users) {
      if (!users) return
      this.$store.dispatch('FiltersStore/TransactionsStore/addUserFilters', users)
    },

    parseQueryParams () {
      let query = {}
      Object.keys(this.$route.query).forEach(key => {
        query[key] = this.$route.query[key]
        if (this.$route.query[key] === 'true') {
          query[key] = true
        } else if (this.$route.query[key] === 'false') {
          query[key] = false
        }
      })
      return query
    },
    close () { },
    async asyncBudgets (event) {
      // this.isSearchingBudgets = true
      this.budgetsQuery = event

      let budgetFilter = {
        name: this.budgetsQuery
      }
      if (this.expenses) {
        budgetFilter['userBudgets.id'] = this.userId
      }

      if (!this.showArchivedBudgetsFlag) {
        budgetFilter['status.code[]'] = 'ACTIVE'
      }
      await this.$store.dispatch('BudgetsStore/setBudgetsFilters', budgetFilter)
      if (!this.showNoBudgetResult) {
        this.$store.dispatch('BudgetsStore/clearStore')
      }
      this.isSearchingBudgets = false
    },
    debounceBudgetSearch (event) {
      if (!event) {
        this.showNoBudgetResult = false

        this.$store.dispatch('BudgetsStore/clearStore')
        return
      }
      this.showNoBudgetResult = true
      if (this.debounceBudgets) {
        this.isSearchingBudgets = false
        clearTimeout(this.debounceBudgets)
      }
      this.isSearchingBudgets = true
      this.debounceBudgets = setTimeout(() => {
        this.asyncBudgets(event)
      }, 350)
    },
    async asyncCustomers (event) {
      this.isSearchingCustomers = true
      const userFilter = {
        name: event
      }
      await this.$store.dispatch('CustomersStore/setCustomersFilters', userFilter)
      this.isSearchingCustomers = false
    },
    async toggleDeletedUsersFlag () {
      this.$store.dispatch('UsersStore/toggleGetDeletedUsersFlag')
      if (this.usersQuery) {
        await this.asyncUsers(this.usersQuery)
      }
    },
    async asyncUsers (event) {
      this.usersQuery = event
      const userFilter = {
        query: this.usersQuery

      }
      await this.$store.dispatch('UsersStore/getUsersSearch', userFilter)
      if (!this.showNoResults) {
        this.$store.dispatch('UsersStore/clearUsersSearchList')
      }
      this.isSearchingUsers = false
    },
    async setCustomerAndFetchFiltersData (event) {
      this.customerId = event
      const usersAndBudgetsFilters = {
        'office.customer.id': this.customerId
      }
      await this.$store.dispatch('UsersStore/setUsersFilters', { filters: usersAndBudgetsFilters })
      await this.$store.dispatch('BudgetsStore/setBudgetsFilters', usersAndBudgetsFilters)
    },
    getSelectedCategoryObject (categoryId, subcategoryId) {
      if (subcategoryId) {
        return this.categoriesAndSubcategoriesOptions.find(
          c => c.subcategoryId === subcategoryId
        )
      } else {
        return this.categoriesAndSubcategoriesOptions.find(
          c => c.categoryId === categoryId
        )
      }
    },
    handleCategorySubcategorySelect (subcategory) {
      this.selectedCategoryObject = subcategory
      this.selectedCategoryId = subcategory.categoryId
      this.selectedSubCategory = subcategory.subcategoryId
    },
    handlePaymentMethodSelect (paymentMethod) {
      this.paymentMethod = paymentMethod
    },
    handleAccountingPeriodSelect (accountingPeriod) {
      if (this.range.start || this.range.end) {
        this.range = {}
      }
      this.accountingPeriodId = accountingPeriod.id
    },
    handleTaxTypeSelect (taxType) {
      this.taxType = taxType
    },
    setField (field, event) {
      if (event && event.target && event.target.value) {
        this[field] = event.target.value
      } else {
        this[field] = event
      }
      if (field === 'reviewStatus') {
        this.parentStep = []
        this.reviewFlowStep = []
        if (event === 'ALL') {
          this.reviewStatus = undefined
          this.reviewStatusTabStatus = 'ALL'
        }
        if (event === 'REVIEWED') {
          this.reviewStatus = [REVIEW_STATUS.APPROVED]
          this.reviewStatusTabStatus = 'REVIEWED'
        }
        if (event === 'NOT_REVIEWED') {
          this.reviewStatus = [REVIEW_STATUS.AWAITING_REVIEW, REVIEW_STATUS.OFFICE_FLOW_REVISION]
          this.reviewStatusTabStatus = 'NOT_REVIEWED'
        }
      }
    },
    handleNonNumeric (event) {
      event = event || window.event
      var charCode = event.keyCode
      if (((charCode >= 48 && charCode <= 57)) && charCode !== 45) {
        return true
      } else {
        event.preventDefault()
      }
    },
    handleAmountBlur (event, field) {
      if (!event.target.value) {
        this[field] = undefined
      }
    },
    checkReviewStatus (reviewStatus) {
      return this.reviewStatusTabStatus === reviewStatus
    },

    async reset () {
      Object.assign(this.$data, initialDataState())
      this.$store.dispatch('TransactionsStore/resetFilters')
      this.$store.dispatch('CompanyStore/TransactionFieldsStore/resetReviewFlows')
      this.$router.replace({ query: {} }).then(
        this.$emit('resetTransactionsFilters', true),
        this.$emit('filtersUpdated')
        // eslint-disable-next-line
      ).catch(err => { })
    },
    handleSetFilters () {
      if (this.createNewQuickFilterFlag) {
        this.showCreateQuicKFilterModal()
      } else {
        this.setFilters()
      }
    },
    handleCreateFilteredView (event) {
      this.customFilteredView = event
      this.setFilters()
    },
    handleRemoveQuickFilter () {
      this.$emit('removeFilteredView', this.quickFilterId)
    },

    async setFilters () {
      if (this.isSubmitting) return
      this.$store.dispatch('TransactionsStore/clearSelectedTransactions')
      this.$store.dispatch('TransactionsStore/clearSelectedUsers')
      this.$store.dispatch('TransactionsStore/toggleSelectAll', { selectAll: false })

      this.isSubmitting = true
      let filters = {}

      if (this.usersSelected.length > 0) {
        filters['card.user.id'] = this.usersSelected.map(item => item.id)
      } else {
        filters['card.user.id'] = undefined
      }

      if (this.accountedAt !== 'undefined') {
        filters['exists[accountedAt]'] = this.accountedAt
      }

      // Esto no se usa (?)
      if (this.transactionStatusId) {
        filters['status.id'] = this.transactionStatusId
      }
      if (this.budgetsSelected.length > 0) {
        filters['budget.id'] = this.budgetsSelected.map(el => el.id)
      } else {
        filters['budget.id'] = undefined
      }

      if (this.customerId) {
        filters['card.office.customer.id'] = this.customerId
      }
      // TODO NEEDS FILTER AMOUNT REFACTOR
      if (this.amountGte !== null && typeof this.amountGte !== 'undefined') {
        filters['absAmount[gte]'] = this.amountGte
      }
      if (this.amountLte !== null && typeof this.amountLte !== 'undefined') {
        filters['absAmount[lte]'] = this.amountLte
      }

      filters['reviewStatusAndNextFlowStepId'] = []

      if (this.userIsInTransactionsSections) {
        if (this.reviewFlows.length > 0) {
          if (this.reviewStatusApproved.checked && !this.reviewStatusPersonalExpense.checked) {
              filters['reviewStatusAndNextFlowStepId'].push(REVIEW_STATUS.APPROVED)
              filters['isPersonalExpense'] = false
          }
          if (!this.reviewStatusApproved.checked && this.reviewStatusPersonalExpense.checked) {
             if (!filters['reviewStatusAndNextFlowStepId'].includes(REVIEW_STATUS.APPROVED)) {
              filters['reviewStatusAndNextFlowStepId'].push(REVIEW_STATUS.APPROVED)
             }
              filters['isPersonalExpense'] = true
          }

          if (this.reviewStatusApproved.checked && this.reviewStatusPersonalExpense.checked) {
            filters['reviewStatusAndNextFlowStepId'].push(REVIEW_STATUS.APPROVED)
            filters['isPersonalExpense'] = undefined
          }
          if (!this.reviewStatusApproved.checked && !this.reviewStatusPersonalExpense.checked) {
           filters['reviewStatusAndNextFlowStepId'] = filters['reviewStatusAndNextFlowStepId'].filter(el => el !== REVIEW_STATUS.APPROVED)
           // delete filters['isPersonalExpense']
           filters['isPersonalExpense'] = undefined
          }

        if (this.reviewFilterDefaultFlow.checked) {
            filters['reviewStatusAndNextFlowStepId'].push(REVIEW_STATUS.AWAITING_REVIEW)
        }
        if (this.reviewFlowStep.length > 0) {
          filters['reviewStatusAndNextFlowStepId'].push(...this.reviewFlowStep.map(el => `OFFICE_FLOW_REVISION_${el}`))
        }
        } else {
          if (this.personalExpenseRegularFilterFlag) {
            filters['reviewStatus.code'] = REVIEW_STATUS.APPROVED
            filters['isPersonalExpense'] = true
          } else {
            filters['reviewStatus.code'] = undefined
            filters['isPersonalExpense'] = undefined
          }

          if (this.reviewStatusTabStatus !== 'ALL') {
              filters['reviewStatus.code'] = this.reviewStatus
          }
        }
      } else {
        if (this.reviewFlows.length > 0) {
          if (this.reviewStatusApproved.checked) {
            filters['reviewStatusAndNextFlowStepId'].push(REVIEW_STATUS.APPROVED)
          }
          if (this.reviewStatusCancelled.checked) {
            filters['reviewStatusAndNextFlowStepId'].push(REVIEW_STATUS.CANCELLED)
          }
          if (this.reviewFilterDefaultFlow.checked) {
            filters['reviewStatusAndNextFlowStepId'].push(REVIEW_STATUS.AWAITING_REVIEW)
        }
        if (this.reviewFlowStep.length > 0) {
          filters['reviewStatusAndNextFlowStepId'].push(...this.reviewFlowStep.map(el => `OFFICE_FLOW_REVISION_${el}`))
        }
        } else {
      if (this.reviewStatus !== undefined) {
        filters['reviewStatus.code'] = this.reviewStatus
      }
        }
      }

      filters['isCompleted'] = this.categorizeStatus

      filters['withWarnings'] = this.withWarnings

      filters['expense.paymentMethod.officePaymentMethod.id'] = this.paymentMethod?.id

      if (this.selectedSubCategory > 0) {
        filters['customCategory.id'] = this.selectedSubCategory
      } else {
        filters['customCategory.id'] = undefined
      }

      if (this.accountingPeriodId !== null) {
        const accountingPeriod = this.accountingPeriods.find(
          ap => ap.id === this.accountingPeriodId
        )
        if (accountingPeriod && accountingPeriod.startDate !== null) {
          filters['receipt.accountingDate[after]'] = accountingPeriod.startDate
        }
        if (accountingPeriod && accountingPeriod.endDate !== null) {
          filters['receipt.accountingDate[before]'] = accountingPeriod.endDate
        }

        // To keep ID on URL in order to initialize, this filter doesnt exist in API
        if (accountingPeriod.id) {
          filters['accountingPeriodId'] = accountingPeriod.id
        }
      }

      this.taxTypes.forEach(taxType => {
        if (taxType.field === null) {
          return
        }
        if (taxType.field && this.taxType !== null && this.taxType.value !== null) {
          // filters[`receipt.${taxType.field}[gt]`] = null

          filters[`receipt.${this.taxType.field}[gt]`] = 0
        } else {
          filters[`receipt.${taxType?.field}[gt]`] = undefined
        }
      })

      filters['receipt.type'] = this.isBill

      await this.$store.dispatch('TransactionsStore/setTransactionsFilters', { filters: filters, force: true, budget: this.isBudget, mergeFilters: true })
      this.$store.dispatch('TransactionsStore/setUserSelectedFiltersAmount', this._countFilters(filters))
      if (this.customFilteredView) {
        let newFilteredView = {
          name: this.customFilteredView,
          section: this.$route.meta.quickFilterSection || false,
          filters: { ...filters }

        }
        if (filters['card.user.id']) {
          newFilteredView['users'] = [...this.usersSelected]
        }
        if (filters['budget.id']) {
          newFilteredView['budget'] = [...this.budgetsSelected]
        }
        if (this.accountingPeriodId) {
          newFilteredView['accountingPeriodId'] = this.accountingPeriodId
        }

        this.$store.dispatch('TransactionsStore/createFilteredView', newFilteredView)
        this.$root.$emit('refreshQuickFilterContainer', this.$store.getters['TransactionsStore/filteredViews'])
      } else if (this.quickFilterId) {
        let newFilters = {
          id: this.quickFilterId,
          name: this.selectedFilteredView.name,
          section: this.$route.meta.quickFilterSection || false,
          filters: {
            ...filters
          }
        }
        if (filters['card.user.id']) {
          newFilters['users'] = [...this.usersSelected]
        }
        if (filters['budget.id']) {
          newFilters['budget'] = [...this.budgetsSelected]
        }
        if (this.accountingPeriodId) {
          newFilters['accountingPeriodId'] = this.accountingPeriodId
        }

        this.$store.dispatch('TransactionsStore/editFilteredView', newFilters)
      }
      if (this.reviewStatus) {
        this.$emit('reviewStatusFilterSelected')
      }

      this.putFiltersUrl(filters)

      let days = null
      if (this.dateAfter && this.dateBefore) {
        days = this.$moment.utc(this.dateBefore).diff(this.dateAfter, 'days')
      }
      let filterEventInformation = {
        days_in_range: days,
        amount_from: filters['absAmount[gte]'] ? filters['absAmount[gte]'] : null,
        amount_to: filters['absAmount[lte]'] ? filters['absAmount[lte]'] : null,
        user_selected: !!filters['card.user.id'],
        same_user: filters['card.user.id'] === this.$store.getters['LoginStore/loggedUserId'],
        budget_selected: !!filters['budget.id'],
        search: false,
        category_selected: !!(filters['customCategory.id'] || filters['defaultCategoryOffice.id'])
      }

      segmentTrack('Transaction filtered view', filterEventInformation)
      this.isSubmitting = false
      this.$store.dispatch('TransactionsStore/fetchTransactionDashboard', this.expenses)
      this.$emit('filtersUpdated')
      this.closeModal()
    },
    showCreateQuicKFilterModal () {
      this.showCreateNewQuickFilterModalFlag = true
    },
    handleTransactionsReviewStatusFilter (reviewStatus) {
        if (reviewStatus.includes(REVIEW_STATUS.APPROVED)) {
          return {
            reviewStatusAndNextFlowStepId: REVIEW_STATUS.APPROVED,
            isPersonalExpense: false
          }
        }
        if (reviewStatus.includes(REVIEW_STATUS.PERSONAL_EXPENSE)) {
          return {
            reviewStatusAndNextFlowStepId: REVIEW_STATUS.APPROVED,
            isPersonalExpense: true
            }
        }
        if (reviewStatus === 'ALL') {
          return {
            reviewStatusAndNextFlowStepId: REVIEW_STATUS.APPROVED
          }
        }
        return {
          reviewStatusAndNextFlowStepId: reviewStatus
        }
    },
    _initializeFilters () {
      if (this.currentFilters) {
        this.transactionStatusId = this.currentFilters['status.id']
          ? this.currentFilters['status.id']
          : ''
        if (this.currentFilters['budget.id']) {
          if (Array.isArray(this.currentFilters['budget.id'])) {
            this.currentFilters['budget.id'].forEach(budgetId => {
              let budgetToFind = this.budgets.find(el => el.id === parseInt(budgetId))
              if (budgetToFind?.id) {
                this.budget.push(budgetToFind)
              }
            })
          }
          this.budgetId = this.currentFilters['budget.id']
        }
        if (this.currentFilters['date[after]']) {
          this.range = {
            start: new Date(this.currentFilters['date[after]']),
            end: new Date(this.currentFilters['date[before]'])
          }
          this.dateAfter = this.currentFilters['date[after]']
        }
        if (this.currentFilters['date[before]']) {
          this.range = {
            start: new Date(this.currentFilters['date[after]']),
            end: new Date(this.currentFilters['date[before]'])
          }

          this.dateBefore = this.currentFilters['date[before]']
        }
        if (this.currentFilters['exists[accountedAt]'] !== undefined) {
          this.accountedAt = this.currentFilters['exists[accountedAt]']
        }

        if (this.currentFilters['customCategory.id']) {
          this.selectedCategoryObject = this.categoriesAndSubcategoriesOptions.find(
            el => el.subcategoryId === Number.parseInt(this.currentFilters['customCategory.id']))
        }

        if (this.currentFilters['receipt.taxBase1[gt]'] >= 0) {
          this.taxType = this.taxTypes.find(el => el.field === 'taxBase1')
        }
        if (this.currentFilters['receipt.taxBase2[gt]'] >= 0) {
          this.taxType = this.taxTypes.find(el => el.field === 'taxBase2')
        }
        if (this.currentFilters['receipt.taxBase3[gt]'] >= 0) {
          this.taxType = this.taxTypes.find(el => el.field === 'taxBase3')
        }
        if (this.currentFilters['receipt.taxBase4[gt]'] >= 0) {
          this.taxType = this.taxTypes.find(el => el.field === 'taxBase4')
        }

        if (this.currentFilters['card.office.customer.id']) {
          this.customerId = Number.parseInt(this.currentFilters['card.office.customer.id'])
        }
        if (isEqual(this.currentFilters['reviewStatus.code'], [REVIEW_STATUS.APPROVED]) || isEqual(this.currentFilters['reviewStatus.code'], [REVIEW_STATUS.AWAITING_REVIEW, REVIEW_STATUS.OFFICE_FLOW_REVISION])) {
         if (isEqual(this.currentFilters['reviewStatus.code'], [REVIEW_STATUS.APPROVED])) {
            this.reviewStatusTabStatus = 'REVIEWED'
          }

          if (isEqual(this.currentFilters['reviewStatus.code'], [REVIEW_STATUS.AWAITING_REVIEW, REVIEW_STATUS.OFFICE_FLOW_REVISION])) {
           this.reviewStatusTabStatus = 'NOT_REVIEWED'
          }

          this.reviewStatus = this.currentFilters['reviewStatus.code']
        } else if (isEqual(this.currentFilters['reviewStatus.code'], [REVIEW_STATUS.AWAITING_REVIEW, REVIEW_STATUS.OFFICE_FLOW_REVISION])) {
          this.setReviewFlowStepType('FUNNEL')
        } else {
          this.reviewStatus = undefined
          this.reviewStatusTabStatus = 'ALL'
        }

        this.withWarnings = this.currentFilters.withWarnings

        if (this.reviewFlows.length === 0) {
          if (this.currentFilters['reviewStatus.code'] && 'isPersonalExpense' in this.currentFilters) {
            this.personalExpenseRegularFilterFlag = true
          }
          this.reviewStatus = this.currentFilters['reviewStatus.code']
        }

        if (this.currentFilters['expense.paymentMethod.officePaymentMethod.id']) {
          this.paymentMethod = this.paymentMethods.find(item => item.id.toString() === this.currentFilters['expense.paymentMethod.officePaymentMethod.id'])
        }
        if (this.currentFilters['accountingPeriodId']) {
          const accountingPeriod = this.accountingPeriods.find(el => el.id === Number.parseInt(this.currentFilters['accountingPeriodId']))
          this.accountingPeriodId = accountingPeriod ? accountingPeriod.id : null
        }
        if (this.currentFilters['absAmount[gte]'] !== null && typeof this.currentFilters['absAmount[gte]'] !== 'undefined') {
          this.amountGte = Number.parseInt(this.currentFilters['absAmount[gte]'])
        }

        if (this.currentFilters['absAmount[lte]'] !== null && typeof this.currentFilters['absAmount[lte]'] !== 'undefined') {
          this.amountLte = Number.parseInt(this.currentFilters['absAmount[lte]'])
        }
        if (this.currentFilters['isCompleted'] || this.currentFilters['isCompleted'] === false) {
          this.categorizeStatus = this.currentFilters['isCompleted']
        } else {
          this.categorizeStatus = undefined
        }

        if (this.currentFilters['receipt.type']) {
          this.isBill = this.currentFilters['receipt.type']
        }
        if (this.currentFilters['reviewStatusAndNextFlowStepId']) {
          if (this.userIsInTransactionsSections) {
            if (this.reviewFlows.length > 0) {
              // Explicit comparison to ensure is personal expense filter
              if (this.currentFilters['reviewStatusAndNextFlowStepId'].includes(REVIEW_STATUS.APPROVED) && this.currentFilters['isPersonalExpense']) {
                this.reviewStatusPersonalExpense.checked = true
                this.reviewStatusApproved.checked = false
                this.reviewStatusFilter.push(REVIEW_STATUS.APPROVED)
                this.reviewStatusFilter.push(REVIEW_STATUS.PERSONAL_EXPENSE)
              }
              // Explicit comparison to ensure is aproved filter
              if (this.currentFilters['reviewStatusAndNextFlowStepId'].includes(REVIEW_STATUS.APPROVED) && !this.currentFilters['isPersonalExpense']) {
                this.reviewStatusApproved.checked = true
                this.reviewStatusPersonalExpense.checked = false
                this.reviewStatusFilter.push(REVIEW_STATUS.APPROVED)
                this.reviewStatusFilter.push(REVIEW_STATUS.PERSONAL_EXPENSE)
              }

            // IF only review status is detected, check both.
            if (this.currentFilters['reviewStatusAndNextFlowStepId'].includes(REVIEW_STATUS.APPROVED) && this.currentFilters['isPersonalExpense'] === undefined) {
                this.reviewStatusApproved.checked = true
                this.reviewStatusPersonalExpense.checked = true
                this.reviewStatusFilter.push(REVIEW_STATUS.APPROVED)
                this.reviewStatusFilter.push(REVIEW_STATUS.PERSONAL_EXPENSE)
            }
            } else {
              if (this.currentFilters['isPersonalExpense']) {
                this.personalExpenseRegularFilterFlag = true
              } else {
                this.personalExpenseRegularFilterFlag = false
              }
              if (this.currentFilters['reviewStatus.code']) {
                this.reviewStatus = this.currentFilters['reviewStatus.code']
              }
            }
           } else {
            if (this.reviewFlows.length > 0) {
              if (this.currentFilters['reviewStatusAndNextFlowStepId'].includes(REVIEW_STATUS.APPROVED)) {
                this.reviewStatusApproved.checked = true
                this.reviewStatusFilter.push(REVIEW_STATUS.APPROVED)
              }
              if (this.currentFilters['reviewStatusAndNextFlowStepId'].includes(REVIEW_STATUS.CANCELLED)) {
                this.reviewStatusCancelled.checked = true
                this.reviewStatusFilter.push(REVIEW_STATUS.CANCELLED)
              }
            } else {
              this.reviewStatus = this.currentFilters['reviewStatus.code']
            }
          }
          // Bloque revisadas

          if ((this.reviewStatusApproved.checked && this.reviewStatusCancelled.checked) || (this.reviewStatusApproved.checked && this.reviewStatusPersonalExpense.checked)) {
            this.reviewFilterTypeNormal.checked = true
          }
          // Bloque en revisión
          if (this.currentFilters['reviewStatusAndNextFlowStepId'].includes(REVIEW_STATUS.AWAITING_REVIEW)) {
            this.reviewFilterDefaultFlow.checked = true
            this.reviewStatusFilter.push(REVIEW_STATUS.AWAITING_REVIEW)
          }

          this.reviewFlows.forEach(reviewFlow => {
            reviewFlow.steps.forEach((step) => {
              if (this.currentFilters['reviewStatusAndNextFlowStepId'].includes(`OFFICE_FLOW_REVISION_${step.id.toString()}`)) {
                step.selected = true
                this.reviewFlowStep.push(step.id.toString())
              }
            })
          })
          this._checkAllStepsSelected()

          if (this.selectedFilteredView && this.selectedFilteredView['users']) {
            this.addUser(this.selectedFilteredView['users'])
          }
          if (this.selectedFilteredView?.budget) {
            this.budget = this.selectedFilteredView.budget
          }
          if (this.selectedFilteredView?.accountingPeriodId) {
            this.accountingPeriodId = this.selectedFilteredView.accountingPeriodId
          }
        }
      }
    },
    mergeDefaultCategoriesOffice () {
      this.categories.forEach(category => {
        let defaultCategoryOffice = this.defaultCategoriesOffice.find(item => item.id === category.id)
        category.defaultCategoryOffice = defaultCategoryOffice || {}
      })
    },
    mergeCustomCategories () {
      this.categories.forEach(category => {
        let customCategories = this.customCategories.filter(item => item.defaultCategoryOffice?.id === category.id)

        category.customCategories = customCategories || {}
      })
    },
    putFiltersUrl (filters) {
      // delete filters['card.user.id']
      this.$router.replace({ query: { ...filters } }).catch(() => { })
    },
    setReviewFilterDefault () {
      this.reviewFilterDefaultFlow.checked = !this.reviewFilterDefaultFlow.checked
    },
    async fetchPaymentMethods () {
      let filters = {}
      if (this.isUser) {
        filters['user.id'] = this.$route?.params?.id || this.$store.getters['LoginStore/loggedUserId']
      }

      await this.$store.dispatch('TransactionsStore/fetchOfficePaymentMethods', filters)
    },
    handleClose () {
      this.$emit('closeModal')
      this.closeModal()
    }

  },

  created () {
    if (!this.isSuperBookKeeper) {
      this.$store.dispatch('AccountingStore/fetchAccountingPeriods', false)
    }
    let promises = [this.$store.dispatch('CategoriesStore/fetchCustomCategories', false), this.$store.dispatch('CategoriesStore/fetchDefaultCategoriesOffice')]
    if (!this.$route?.meta?.users && this.showPaymentMethodsFilter) {
      promises.push(this.fetchPaymentMethods())
    }

    promises.push(this.$store.dispatch('CompanyStore/TransactionFieldsStore/fetchReviewFlows'))

    Promise.allSettled(promises).then(() => {
      this.mergeDefaultCategoriesOffice()
      this.mergeCustomCategories()
      this._initializeSelected()
      this._initializeFilters()
      this.loading = false
    })
  },

  beforeRouteUpdate (to, from, next) {
    this.closeModal()
    next()
  },
  watch: {
    showArchivedBudgetsFlag: async function (newV) {
      if (this.budgetsQuery) {
         await this.asyncBudgets(this.budgetsQuery)
      }
    }
  }

}
</script>

<style lang="scss" scoped>
.Fuell-Text--smaller {
  font-size: 12px !important;
  white-space: nowrap;
}

.review-flow--tree,
.review-flow--tree-step-container {
  margin-bottom: 8px;
  padding: 0;
  padding-left: 4px;
  list-style-type: none;

  li {
    display: flex;
    justify-content: flex-start;

    .list-header {
      width: 100%;
      font-weight: 500;
      color: var(--text-heading);
      padding-left: 0px !important;
      margin-left: 3px !important;
      margin-bottom: 3px;
    }

    .option {
      margin-left: 10px;
      line-height: 24px;
    }
  }

  .label-tree {
    padding-left: 27px;
    font-size: 15px;
    line-height: 24px;
    color: var(--text-body);
    cursor: pointer;
    &-title {
      color: var(--text-heading);
    }

    &-bottom {
      margin-bottom: 10px;
    }
  }
}

.review-flow--tree {
  margin-bottom: 0;
}

.review-flow--tree-step-container {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-direction: column;
  padding-left: 27px;
}

.Link {
  cursor: pointer;
}

.Radiobutton {
  border-radius: 15px;
}

.Radiobutton--selected:after {
  border-radius: 15px;
  width: 8px;
  height: 8px;
  left: 3px;
  top: 3px;
}

.custom-category-option {
  font-weight: 300;
  font-size: 15px;
  position: relative;
  height: 34px;
}

.custom-category-option.is-parent {
  font-weight: 500;
}

.custom-category-option.is-parent .category-name {
  margin-top: 8px;
}

.custom-category-option .parent-category-name {
  position: absolute;
  font-size: 11px;
  top: 0px;
}

.custom-category-option .category-name {
  position: absolute;
  margin-top: 15px;
}

.custom-category-selected {
  font-weight: 300;
  font-size: 15px;
  position: relative;
}

.custom-category-selected.is-parent {
  font-weight: 500;
}

.custom-category-selected.is-parent .category-name {
  margin-top: 0px;
}

.custom-category-selected .parent-category-name {
  position: absolute;
  font-size: 11px;
  top: -8px;
}

.custom-category-selected .category-name {
  position: absolute;
  margin-top: 6px;
  white-space: nowrap;
  overflow: hidden;
  width: 100%;
  text-overflow: ellipsis;
}

.Form-field--placeholder {
  color: var(--text-placeholder);
  font-size: 15px;
  font-weight: 400;
}

.Form-field--review-block {
  margin: 0px;
  height: 100%;
}

.review-flow--tree-header {
  font-size: 15px;
  color: var(--text-body);
  font-weight: 400;
}

.arrow-content-container {
  position: relative;
  right: 18px;
  top: 15px;
  width: 20px;
  left: calc(100% - 40px);
  transform: rotate(180deg);
}

.content-shown {
  margin-top: -20px;
}

.info-container {
  height: 16px;
  width: 16px;
  margin-left: 5px;
  margin-top: 2px;
}

.filter-placeholder--active {
  color: var(--text-body);
  font-weight: 400;
}

.filter-placeholder {
  font-weight: 300;
}

.text-remove {
  font-weight: 400;
  color: var(--semantic-negative-main);
}
.Tabs-itmLink {
  font-weight: 400;
  font-size: 15px;
  line-height: 24px;
}
.Tab-item-first {
  width: 101px;
}
.Tab-item-second {
  width: 136px;
}
.Tab-item-third {
  width: 150px;
}
</style>
<style lang="scss">
.Fuell-Dropdown--content--less-padding {
  text-align: left;
  padding-left: 10px;
  padding-right: 5px;
}

.multiselect__strong {
  display: none;

  &:last-child {
    display: inline-block;
  }
}
.ModalBox--r-filters {
  width: 420px;
}
.ModalBox--r--footer {
  margin: 0px;
  padding: 16px;
}
.dropdown-before-list--block {
  padding: 24px 196px 12px 12px;
  height: 24px;

  color: var(--text-heading);
}
.dropdown-separator-block {
  padding: 12px;

  &--separator-bar {
    height: 1px;
    background: var(--fill-border);
  }
}
.multiselect-option-max-w {
  max-width: 256px;
}

.multiselect-deleted-label {
  color: var(--text-placeholder);
  font-size: 12px;
  font-style: italic;
  margin-right: 10px;
  margin-left: 8px;
}
</style>
